// Moist adiabatic lapse rate dT/dp for a saturated parcel.
// Source: ported from src/skewt/bundle.js (function moistGradientT), which
// is the existing battle-tested implementation in this codebase. Skysight's
// equivalent (wetAdiabaticSlope in their bundle) uses a virtual-temperature
// form that produces near-identical values at the precision we render.

const EPSILON = 0.622;            // Mw / Md (water vapor / dry air molar mass ratio)
const Rd = 287.0;                  // dry-air gas constant, J/(kg·K)
const Cpd = 1005.0;                // dry-air specific heat at constant p, J/(kg·K)
const CELSIUS_TO_K = 273.15;
const SAT_PRESSURE_0C = 6.112;     // saturation vapor pressure at 0 °C, hPa

/** Saturation vapor pressure (Bolton 1980), hPa. tK is temperature in Kelvin. */
function saturationVaporPressure(tK) {
  const tC = tK - CELSIUS_TO_K;
  return SAT_PRESSURE_0C * Math.exp((17.67 * tC) / (tC + 243.5));
}

/** Saturation mixing ratio, dimensionless. */
function saturationMixingRatio(p, tK) {
  const e = saturationVaporPressure(tK);
  return (EPSILON * e) / (p - e);
}

/** Latent heat of vaporization of water, J/kg. Slight T-dependence. */
function latentHeat(tC) {
  return (2502.2 - 2.43089 * tC) * 1000;
}

/**
 * Moist adiabatic lapse rate dT/dp for a saturated parcel.
 * Inputs in (°C, hPa), output in (°C / hPa) — positive (T decreases as p decreases).
 */
export function wetAdiabaticSlope(t, p) {
  const tK = t + CELSIUS_TO_K;
  const rs = saturationMixingRatio(p, tK);
  const Lv = latentHeat(t);
  const numerator = Rd * tK + Lv * rs;
  const denominator = Cpd + (Lv * Lv * rs * EPSILON) / (Rd * tK * tK);
  return (1 / p) * (numerator / denominator);
}
